import React, { useEffect } from "react";
import {
  Stack,
  Box,
  Text,
  Flex,
  useBreakpointValue,
  StackDirection,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { DeviceFrameset } from "react-device-frameset";
import ImageWithModal from "./ImageWithModal";
import Carousel from "./Carousel";
import InstagramStoryMockup from "./InstagramStoryMockup";
import {
  SocialData,
  MarketplacePreview as MarketplacePreviewType,
  Listing,
} from "../types/types";
import InstagramStoryFallback from "./InstagramStoryFallback";
import posthog from "posthog-js";

interface PreviewFrame {
  loading: boolean;
  image?: string;
  alt: string;
  caption: React.ReactNode;
  delay: number;
}

interface MarketplacePreviewProps {
  marketplacePreviewImageLoading: boolean;
  sellerPreviewImageLoading: boolean;
  payoutsPreviewImageLoading: boolean;
  listingPromoPreviewImagesLoading: boolean;
  marketplacePreviewImage?: string;
  sellerPreviewImage?: string;
  payoutsPreviewImage?: string;
  listingPromoPreviewImages?: string[];
  username: string;
  marketplacePreview: MarketplacePreviewType;
  socialData: SocialData;
  showFallbackInstagram: boolean;
  listings: Listing[];
}

export const MarketplacePreview: React.FC<MarketplacePreviewProps> = ({
  marketplacePreviewImageLoading,
  sellerPreviewImageLoading,
  payoutsPreviewImageLoading,
  listingPromoPreviewImagesLoading,
  marketplacePreviewImage,
  sellerPreviewImage,
  payoutsPreviewImage,
  listingPromoPreviewImages,
  username,
  marketplacePreview,
  socialData,
  showFallbackInstagram,
  listings
}) => {
  const marketplaceLogo = marketplacePreview?.logo_image;
  const anyImageLoading =
    marketplacePreviewImageLoading ||
    sellerPreviewImageLoading ||
    payoutsPreviewImageLoading ||
    listingPromoPreviewImagesLoading;

  useEffect(() => {
    posthog.capture("phone_screens_shown", {
      loma_ai_event: true
    });
  }, []);

  useEffect(() => {
    if (!anyImageLoading) {
      posthog.capture("phone_images_loaded", {
        loma_ai_event: true,
        seller_preview_image: sellerPreviewImage,
        marketplace_preview_image: marketplacePreviewImage,
        payouts_preview_image: payoutsPreviewImage,
        listing_promo_preview_images: listingPromoPreviewImages,
      });
    }
  }, [anyImageLoading]);

  // Responsive values based on screen size
  const deviceScale =
    useBreakpointValue({
      base: 0.7,
      sm: 0.7,
      md: 0.8,
      lg: 0.5,
    }) || 0.5;

  const captionMarginTop =
    useBreakpointValue({
      base: "-200px",
      sm: "-200px",
      md: "-150px",
      lg: "-400px",
    }) || "-400px";

  const stackDirection =
    useBreakpointValue({
      base: "column" as StackDirection,
      md: "column" as StackDirection,
      lg: "row" as StackDirection,
    }) || ("row" as StackDirection);

  const deviceMarginBottom =
    useBreakpointValue({
      base: "-120px",
      sm: "-120px",
      md: "-120px",
      lg: "-120px",
    }) || "-120px";

  const frameSpacing =
    useBreakpointValue({
      base: 20,
      sm: 16,
      md: 10,
      lg: 8,
    }) || 8;

  // covers both marketplace and classifieds
  let homeScreenAction = "buy and sell with each other";
  if (socialData?.commerce_modality === "directory") {
    homeScreenAction = "find and connect with service providers";
  } else if (socialData?.commerce_modality === "auction") {
    homeScreenAction = "bid on and buy items";
  }
  const homeScreenHeader =
    marketplacePreview?.ui_text?.home_header ?? "Your Web App";
  const homeScreenCaption =
    marketplacePreview?.ui_text?.home_caption ??
    `We're building a mobile-friendly web app so your community can ${homeScreenAction}`;
  const sellerPreviewHeader =
    marketplacePreview?.ui_text?.seller_header ?? "Supply-Side View";
  const sellerPreviewCaption =
    marketplacePreview?.ui_text?.seller_caption ??
    `Fields were added to ${
      marketplacePreview?.categories[0].name ?? "your niche"
    } to enhance listings and enable filtering`;
  const promoMediaHeader =
    marketplacePreview?.ui_text?.promo_header ?? "Social Media";
  const promoMediaCaption =
    marketplacePreview?.ui_text?.promo_caption ??
    `Marketing campaigns that engage your audience can be generated and scheduled automatically`;
  const appPreviewImages = [
    marketplacePreviewImage,
    payoutsPreviewImage,
  ] as string[];
  const appSellerImages = [
    sellerPreviewImage,
  ] as string[];

  const previewFrames: PreviewFrame[] = [
    {
      loading: marketplacePreviewImageLoading,
      image: marketplacePreviewImage,
      alt: "Business Preview",
      caption: (
        <>
          <Text fontWeight="bold" mb={1} pt={4}>
            {homeScreenHeader}
          </Text>
          {homeScreenCaption}
        </>
      ),
      delay: 0,
    },
    {
      loading: sellerPreviewImageLoading,
      image: sellerPreviewImage,
      alt: "Seller Preview",
      caption: (
        <>
          <Text fontWeight="bold" mb={1} pt={4}>
            {sellerPreviewHeader}
          </Text>
          {sellerPreviewCaption}
        </>
      ),
      delay: 1,
    },
    {
      loading: listingPromoPreviewImagesLoading,
      image: listingPromoPreviewImages?.[0],
      alt: "Listing Promo Preview",
      caption: (
        <>
          <Text fontWeight="bold" mb={1} pt={4}>
            {promoMediaHeader}
          </Text>
          {promoMediaCaption}
        </>
      ),
      delay: 2,
    },
  ];

  return (
    <Stack
      direction={stackDirection}
      spacing={frameSpacing}
      alignItems="flex-start"
      w="full"
      justify="center"
      px={{ base: 2, md: 3 }}
      pt={0}
    >
      {previewFrames.map((frame, index) => (
        <Flex
          key={index}
          direction="column"
          align="center"
          w="full"
          maxW={{ base: "100%", md: "100%", lg: "28%" }}
          mb={{ base: 12, md: 6, lg: 0 }}
        >
          {/* @ts-ignore */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1.5,
              delay:
                useBreakpointValue({
                  base: index * 0.5,
                  md: index * 0.8,
                  lg: index * 1.2,
                }) || index * 1.2,
              ease: "easeOut",
            }}
          >
            <Box
              transform={`scale(${deviceScale})`}
              transformOrigin="top center"
              width="fit-content"
              mb={deviceMarginBottom}
              mx="auto"
              position="relative"
              maxWidth="100%"
              overflow="hidden"
              mt={0}
            >
              <DeviceFrameset device="iPhone X">
                {anyImageLoading ? (
                  <Flex justify="center" align="center" h="100%">
                    {/* @ts-ignore */}
                    <l-chaotic-orbit size="60" speed="1.5" color="black" />
                  </Flex>
                ) : index === 2 && listingPromoPreviewImages?.length ? (
                  <Box position="relative" h="100%" w="100%" bg="black">
                    <Flex
                      justify="center"
                      align="center"
                      h="100%"
                      w="100%"
                      pt={8}
                    >
                      <Carousel
                        key="promo-carousel"
                        cards={listingPromoPreviewImages.map((image, i) => (
                          <InstagramStoryMockup
                            key={`promo-${i}`}
                            imageUrl={image}
                            profileUrl={marketplaceLogo || ""}
                            username={marketplacePreview?.name || username}
                            timestamp="2h ago"
                          />
                        ))}
                      />
                    </Flex>
                  </Box>
                ) : index === 2 && showFallbackInstagram ? (
                  <Box position="relative" h="100%" w="100%" bg="black">
                    <Flex
                      justify="center"
                      align="center"
                      h="100%"
                      w="100%"
                      pt={8}
                    >
                      <InstagramStoryFallback
                        profileUrl={marketplaceLogo || ""}
                        username={marketplacePreview?.name || username}
                        timestamp="2h ago"
                        listings={listings}
                      />
                    </Flex>
                  </Box>
                ): index === 0 || index === 1 ? (
                  <Box position="relative" h="100%" w="100%" overflow="hidden">
                    <Flex justify="center" align="center" h="100%" w="100%">
                      <Carousel
                        key="app-carousel"
                        cards={
                          (index == 0 ? appPreviewImages : appSellerImages).map((image, i) => (
                          <ImageWithModal
                            key={`app-${index}-${i}`}
                            isScreenshot={true}
                            image={image || ""}
                            fullSizeImage={image || ""}
                            alt={"App Preview"}
                          />
                        ))}
                      />
                    </Flex>
                  </Box>
                ) : (
                  <ImageWithModal
                    key={`carousel-${index}`}
                    isScreenshot={index === 0 || index === 1}
                    image={frame.image || ""}
                    fullSizeImage={frame.image || ""}
                    alt={frame.alt || ""}
                  />
                )}
              </DeviceFrameset>
            </Box>

            <Box
              textAlign="center"
              minW={{ base: "300px", sm: "300px", lg: "260px" }}
              maxW={{ base: "300px", sm: "400px", lg: "260px" }}
              mt={captionMarginTop}
              px={{ base: 2, md: 4 }}
              mx="auto"
              position="relative"
              zIndex={1}
            >
              <Text
                as={motion.p}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                fontSize={{ base: "lg", md: "md" }}
                color="white"
                px={1}
              >
                {frame.caption}
              </Text>
            </Box>
          </motion.div>
        </Flex>
      ))}
    </Stack>
  );
};
